import { cycleInfo } from "./cycle";
import { parseDay, weekDays } from "./date";
import { beforePlan, doseIndex, neutralDay, sessionKeyFor, typeName, weekMapFor } from "./plan";
import { dayProgress, dayQueue, queueMinutes, sessionMinutes, sessionPct } from "./queue";
import { dayFull } from "./stats";
import type { KayaState, SessionKey } from "./types";

/**
 * How a day of the week stands, in the order the checks are made: a finished session beats a
 * rescue or a freeze (the day is simply done), and a past day with nothing on it is only "missed"
 * when it was not a rest or period day — the same rule `missedDays` uses for the streak. A day
 * before the plan's start with nothing on it is "before": never scheduled, so never missed.
 */
export type WeekDayState =
  | "done"
  | "rescued"
  | "frozen"
  | "rested"
  | "started"
  | "missed"
  | "planned"
  | "rest"
  | "before";

export interface SessionProgress {
  done: number;
  total: number;
}

export interface WeekRow {
  ds: string;
  key: SessionKey;
  name: string;
  isToday: boolean;
  state: WeekDayState;
  /** 0..1 for the ring; 1 once the day counts as done */
  pct: number;
  minutes: number;
  progress: SessionProgress | null;
  /** the session was hand-picked for the day */
  swapped: boolean;
  travel: boolean;
  treadmill: boolean;
  /** a period day, when cycle tracking is on */
  period: boolean;
  /** the day falls before `profile.startDate` — outside the week's "n of m" */
  beforePlan: boolean;
}

/**
 * Work steps ticked off out of those the session has, counted the way `sessionPct` counts them —
 * so "6 of 14" and the ring never disagree. Null for a session with no work (a rest day).
 */
export function sessionProgress(S: KayaState, ds: string, key: SessionKey): SessionProgress | null {
  // read against what the day ran, when it froze it (ADR-050)
  return dayProgress(S, ds, key);
}

/**
 * The real length of a day's session in minutes (ADR-050): the queue it ran when it froze one,
 * today's otherwise. Zero for a rest day.
 */
export function dayMinutes(S: KayaState, ds: string, key: SessionKey, simple?: boolean): number {
  const dq = dayQueue(S, ds, key);
  return dq.queue ? queueMinutes(S, dq.queue, simple) : sessionMinutes(S, key, doseIndex(S, key, ds), undefined, simple);
}

function dayState(S: KayaState, ds: string, today: string, key: SessionKey, pct: number | null): WeekDayState {
  const L = S.logs[ds];
  if (dayFull(S, ds)) return "done";
  if (L?.rescued) return "rescued";
  if (L?.frozen) return "frozen";
  if (L?.periodSkip) return "rested";
  if (pct) return "started";
  if (beforePlan(S, ds)) return "before";
  if (key === "R") return "rest";
  if (ds < today) return neutralDay(S, ds) ? "rest" : "missed";
  return "planned";
}

/** Monday to Sunday of the week holding `today`, each day with its session and how it stands. */
/** `simple`: the phone's player mode, so a day's minutes match Train's hero and the start screen. */
export function weekRows(S: KayaState, today: string, simple?: boolean): WeekRow[] {
  return weekDays(parseDay(today)).map((ds) => {
    const key = sessionKeyFor(S, ds);
    const pct = sessionPct(S, ds, key);
    const state = dayState(S, ds, today, key, pct);
    const L = S.logs[ds];
    return {
      ds,
      key,
      name: typeName(key),
      isToday: ds === today,
      state,
      pct: state === "done" ? 1 : pct ?? 0,
      minutes: dayMinutes(S, ds, key, simple),
      progress: sessionProgress(S, ds, key),
      swapped: !!S.overrides[ds],
      travel: !!S.travelDates[ds],
      treadmill: !!L?.treadmill,
      period: !!cycleInfo(S, ds)?.inPeriod,
      beforePlan: beforePlan(S, ds),
    };
  });
}

/** "2 of 6 done": a rescued day counts, because the rescue made it count for the streak; a freeze
 *  only protects the streak, so it does not. Rest days are not in either figure, and nor are days
 *  before the plan's start, so a week that begins on a Wednesday is out of Wednesday to Sunday. */
export function weekDone(rows: WeekRow[]): { done: number; of: number } {
  const inPlan = rows.filter((r) => !r.beforePlan);
  return {
    done: inPlan.filter((r) => r.state === "done" || r.state === "rescued").length,
    of: inPlan.filter((r) => r.key !== "R").length,
  };
}

/** What the plan says for `ds` with the day's own changes — a swap, a travel day — taken away. */
export function plannedKey(S: KayaState, ds: string): SessionKey {
  const overrides = { ...S.overrides };
  delete overrides[ds];
  const travelDates = { ...S.travelDates };
  delete travelDates[ds];
  return sessionKeyFor({ ...S, overrides, travelDates }, ds);
}

/**
 * The override that turns the gentle session off again, or null to just clear the override.
 *
 * On a period day the plan itself is the gentle session, so clearing would change nothing; the
 * weekday's normal session has to be picked explicitly instead (Home's "Normal session").
 */
export function gentleOffKey(S: KayaState, ds: string): SessionKey | null {
  if (plannedKey(S, ds) !== "P") return null;
  const w = weekMapFor(S)[parseDay(ds).getDay()];
  return w === "R" ? "C" : w;
}

/** Kept here for the walk list's import; the one definition lives in `date.ts`. */
export { clockTime } from "./date";
